import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiHome as Home,
  FiHeart as Heart,
  FiShield as Shield,
  FiArrowRight as ArrowRight,
} from 'react-icons/fi';
import {
  FaCar as Car,
  FaBuilding as Building,
  FaUmbrella as Umbrella,
} from 'react-icons/fa';
import Footer from '../components/Footer';

const ServicesPage = () => {
  const navigate = useNavigate();
  const goToQuote = () => navigate('/quote');

  const services = [
    {
      icon: Car,
      title: 'Personal Auto',
      description:
        'Comprehensive coverage for cars, trucks, and motorcycles. Liability, collision, and comprehensive options with competitive rates from multiple carriers.',
      highlights: ['Liability & full coverage', 'Multi-vehicle discounts', 'SR-22 filings available'],
    },
    {
      icon: Home,
      title: 'Homeowners / Renters',
      description:
        'Protect your home, condo, or rental. Dwelling, personal property, and liability coverage tailored to your living situation.',
      highlights: ['Dwelling & contents', 'Loss of use', 'Personal liability'],
    },
    {
      icon: Building,
      title: 'Commercial',
      description:
        'Business Owners Policies (BOP), general liability, workers\u2019 comp, and commercial auto. Coverage solutions built for your business.',
      highlights: ['Business Owners Policy', 'General liability', 'Workers\u2019 compensation'],
    },
    {
      icon: Heart,
      title: 'Life Insurance',
      description:
        'Term and whole life policies to secure your family\u2019s future. Affordable protection with flexible options to fit your budget.',
      highlights: ['Term life', 'Whole life', 'Flexible benefit amounts'],
    },
  ];

  const specialtyLines = [
    { icon: Umbrella, title: 'Umbrella / Excess', desc: 'Extra liability coverage above your standard policy limits.' },
    { icon: Shield, title: 'Specialty Lines', desc: 'Motorcycle, RV, boat, and other unique coverage needs.' },
  ];

  return (
    <div className="min-h-screen">
      {/* Header */}
      <section className="pt-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            Our Insurance Services
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Comprehensive coverage options for every part of your life. We shop top-rated carriers to find you the best protection at the best price.
          </p>
        </div>
      </section>

      {/* Service tiles */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                className="bg-white p-8 rounded-xl border border-gray-200 hover:shadow-lg transition-all duration-200 card-hover group flex flex-col"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-[#DC143C] to-[#B01030] rounded-lg flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-200">
                  <service.icon size={32} className="text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                <p className="text-gray-600 mb-4 flex-grow">{service.description}</p>
                <ul className="space-y-1 mb-6">
                  {service.highlights.map((h, i) => (
                    <li key={i} className="text-sm text-gray-500 flex items-center gap-2">
                      <span className="w-1.5 h-1.5 bg-[#DC143C] rounded-full" />
                      {h}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={goToQuote}
                  className="text-[#DC143C] font-semibold group-hover:underline flex items-center gap-1"
                >
                  Get a Free Quote <ArrowRight size={16} />
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Specialty lines */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Specialty Lines</h2>
            <p className="text-lg text-gray-600">Additional coverage options for unique needs</p>
          </div>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {specialtyLines.map((line, index) => (
              <div
                key={index}
                className="bg-white p-8 rounded-xl border border-gray-200 hover:shadow-lg transition-all duration-200 flex items-start gap-6"
              >
                <div className="w-14 h-14 bg-[#0038A8] rounded-lg flex items-center justify-center flex-shrink-0">
                  <line.icon size={28} className="text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{line.title}</h3>
                  <p className="text-gray-600">{line.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Ready to Get Covered?</h2>
          <p className="text-lg text-gray-600 mb-8">
            Get a free quote in minutes. Our agents will compare rates from multiple carriers to find you the best coverage.
          </p>
          <button
            onClick={goToQuote}
            className="bg-[#DC143C] text-white px-8 py-4 rounded-lg font-semibold hover:bg-[#B01030] transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-105"
          >
            Get a Free Quote
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServicesPage;
